import React, { useMemo, useState, useCallback } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Bot, Building2, BrainCircuit, ChevronDown, ChevronLeft, ChevronRight, FileText, Globe2, HelpCircle, LogOut, Menu, PanelsTopLeft, Plug, ScrollText, Settings2, Workflow, Wrench } from 'lucide-react';
import { Sheet, SheetContent } from '../components/ui/sheet';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import FacebookPageManager from '../components/FacebookPageManager';
import InstagramAccountManager from '../components/InstagramAccountManager';
import WhatsAppConnectionManager from '../components/WhatsAppConnectionManager';
import { hasPermission, isSuperAdmin } from '../utils/permissionUtils';

/**
 * Route + layout map
 * ------------------
 * - src/App.js mounts AppShell for every authenticated route.
 * - Sidebar links (NavLink) map to:
 *     /inbox[/channel]      -> InboxLayout + InboxWorkspace (chat list + conversation)
 *     /templates            -> TemplatesLayout
 *     /comments             -> CommentsLayout (3 columns)
 *     /user-directory       -> AdminLayout
 *     /positions            -> AdminLayout
 * - Each page renders its module-specific layout inside {children}; AppShell only handles the
 *   persistent chrome (sidebar + mobile header) and theme toggling.
 */
const AppShell = ({ user, navItems = [], onLogout, children, sidebarExtras = null }) => {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [isSidebarExpanded, setSidebarExpanded] = useState(() => {
    return window.localStorage.getItem('ticklegram.sidebar.expanded') === 'true';
  });
  const [showInstagramManager, setShowInstagramManager] = useState(false);
  const [showFacebookManager, setShowFacebookManager] = useState(false);
  const [showWhatsAppManager, setShowWhatsAppManager] = useState(false);
  const [connectedPagesExpanded, setConnectedPagesExpanded] = useState(false);
  const location = useLocation();
  const [metaAgentExpanded, setMetaAgentExpanded] = useState(() => location.pathname.startsWith('/meta-agent'));
  const navigate = useNavigate();

  const visibleNavItems = useMemo(() => {
    const items = [...navItems];
    if (hasPermission(user, 'whatsapp:bot:manage')) {
      items.push({ id: 'whatsapp-bots', label: 'WhatsApp Bots', icon: Workflow, to: '/whatsapp-bots' });
    }
    if (hasPermission(user, 'operations:view')) {
      items.push({ id: 'operations', label: 'Operations', icon: ScrollText, to: '/operations' });
    }
    if (!hasPermission(user, 'meta-agent:manage')) return items;
    return [
      ...items,
      {
        id: 'meta-agent',
        label: 'Meta Agent',
        icon: Bot,
        type: 'meta-agent',
        menuItems: [
          { id: 'settings', label: 'Agent settings', icon: Settings2 },
          { id: 'business-info', label: 'Business info', icon: Building2 },
          { id: 'faqs', label: 'FAQs', icon: HelpCircle },
          { id: 'files', label: 'Knowledge files', icon: FileText },
          { id: 'websites', label: 'Knowledge websites', icon: Globe2 },
          { id: 'skills', label: 'Agent skills', icon: BrainCircuit },
          { id: 'connector-tools', label: 'Connector tools', icon: Wrench },
          { id: 'connectors', label: 'Connectors', icon: Plug },
          { id: 'ui-skills', label: 'UI Skills', icon: PanelsTopLeft },
        ],
      },
    ];
  }, [navItems, user]);

  const toggleSidebar = useCallback(() => {
    setSidebarExpanded((expanded) => {
      const nextExpanded = !expanded;
      window.localStorage.setItem('ticklegram.sidebar.expanded', String(nextExpanded));
      return nextExpanded;
    });
  }, []);

  const roleLabel = useMemo(() => {
    if (user?.position?.name) {
      return user.position.name;
    }
    if (user?.role) {
      return user.role.charAt(0).toUpperCase() + user.role.slice(1);
    }
    return 'User';
  }, [user]);

  const initials = useMemo(() => {
    if (!user?.name) {
      return 'TG';
    }
    return user.name
      .split(' ')
      .map((chunk) => chunk.charAt(0))
      .join('')
      .slice(0, 2)
      .toUpperCase();
  }, [user]);

  const renderAvatar = (sizeClass = 'w-10 h-10') => {
    return (
      <div
        className={cn(
          sizeClass,
          'rounded-full bg-gradient-to-br from-indigo-500 via-purple-500 to-fuchsia-500 text-white flex items-center justify-center text-sm font-bold tracking-wide ring-2 ring-[var(--tg-sidebar-bg)] shadow-[0_0_0_1px_var(--tg-accent-soft),0_10px_25px_rgba(0,0,0,0.3)]'
        )}
      >
        {initials}
      </div>
    );
  };

  const renderLogoutButton = ({ className = '', showLabel = false }) => (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size={showLabel ? 'default' : 'icon'}
          className={className}
        >
          <LogOut className="w-4 h-4" />
          {showLabel && <span>Logout</span>}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-[var(--tg-surface)] border border-[var(--tg-border-soft)] text-[var(--tg-text-primary)]">
        <AlertDialogHeader>
          <AlertDialogTitle>Sign out?</AlertDialogTitle>
          <AlertDialogDescription className="text-[var(--tg-text-muted)]">
            You will be logged out of TickleGram on this device. You can log back in anytime.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="bg-transparent border border-[var(--tg-border-soft)] text-[var(--tg-text-primary)]">
            Stay
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onLogout}
            className="bg-gradient-to-r from-red-500 to-rose-500 text-white hover:from-red-600 hover:to-rose-600"
          >
            Logout
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  const handleManageNavClick = useCallback((destination) => {
    if (destination === 'instagram') {
      setShowInstagramManager(true);
    } else if (destination === 'facebook') {
      setShowFacebookManager(true);
    } else if (destination === 'whatsapp') {
      setShowWhatsAppManager(true);
    }
    setMobileNavOpen(false);
  }, []);

  const renderNavList = (expanded) => (
    <nav className="flex flex-col gap-1 mt-6">
      {visibleNavItems.map((item) => {
        const Icon = item.icon;
        const content = (
          <>
            {Icon && <Icon className="w-4 h-4 shrink-0" />}
            {expanded && <span className="truncate app-nav-label">{item.label}</span>}
            {expanded && item.badge && (
              <span className="app-nav-badge text-xs font-semibold px-2 py-0.5 rounded-full shrink-0">
                {item.badge}
              </span>
            )}
          </>
        );

        if (item.type === 'manage-pages' && item.menuItems?.length) {
          const visibleMenuItems = item.menuItems.filter(
            (menuItem) => !menuItem.superAdminOnly || isSuperAdmin(user)
          );
          if (!visibleMenuItems.length) return null;
          return (
            <div key={item.id} className="app-nav-group">
              <button
                type="button"
                aria-expanded={connectedPagesExpanded}
                aria-label={expanded ? undefined : item.label}
                title={expanded ? undefined : item.label}
                onClick={() => {
                  if (!expanded) {
                    setSidebarExpanded(true);
                    window.localStorage.setItem('ticklegram.sidebar.expanded', 'true');
                  }
                  setConnectedPagesExpanded((isOpen) => !isOpen);
                }}
                className="app-nav-item app-nav-group__trigger flex w-full items-center gap-3 px-2.5 py-1.5 rounded-xl text-sm font-medium transition-colors app-nav-item--idle"
              >
                <div className="flex items-center gap-3 min-w-0">{content}</div>
                {expanded && (
                  <ChevronDown
                    className={cn('w-4 h-4 ml-auto opacity-70 transition-transform', connectedPagesExpanded && 'rotate-180')}
                  />
                )}
              </button>
              {expanded && connectedPagesExpanded && (
                <div className="app-nav-group__items" aria-label="Connected page settings">
                  {visibleMenuItems.map((menuItem) => {
                    const MenuIcon = menuItem.icon;
                    return (
                      <button
                        key={menuItem.id}
                        type="button"
                        className="app-nav-group__item"
                        onClick={() => handleManageNavClick(menuItem.id)}
                      >
                        {MenuIcon && <MenuIcon className="w-4 h-4" />}
                        <span>{menuItem.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        }

        if (item.type === 'meta-agent' && item.menuItems?.length) {
          const isActive = location.pathname.startsWith('/meta-agent');
          return (
            <div key={item.id} className="app-nav-group">
              <button
                type="button"
                aria-expanded={metaAgentExpanded}
                aria-label={expanded ? undefined : item.label}
                title={expanded ? undefined : item.label}
                onClick={() => {
                  if (!expanded) {
                    setSidebarExpanded(true);
                    window.localStorage.setItem('ticklegram.sidebar.expanded', 'true');
                  }
                  setMetaAgentExpanded((isOpen) => !isOpen);
                  if (!isActive) navigate('/meta-agent?section=overview');
                }}
                className={cn(
                  'app-nav-item app-nav-group__trigger flex w-full items-center gap-3 px-2.5 py-1.5 rounded-xl text-sm font-medium transition-colors',
                  isActive ? 'app-nav-item--active' : 'app-nav-item--idle'
                )}
              >
                <div className="flex items-center gap-3 min-w-0">{content}</div>
                {expanded && (
                  <ChevronDown className={cn('w-4 h-4 ml-auto opacity-70 transition-transform', metaAgentExpanded && 'rotate-180')} />
                )}
              </button>
              {expanded && metaAgentExpanded && (
                <div className="app-nav-group__items" aria-label="Meta Agent controls">
                  {item.menuItems.map((menuItem) => {
                    const MenuIcon = menuItem.icon;
                    const selected = new URLSearchParams(location.search).get('section') === menuItem.id
                      || (!new URLSearchParams(location.search).get('section') && menuItem.id === 'overview');
                    return (
                      <button
                        key={menuItem.id}
                        type="button"
                        className={cn('app-nav-group__item', isActive && selected && 'app-nav-item--active')}
                        onClick={() => {
                          navigate(`/meta-agent?section=${menuItem.id}`);
                          setMobileNavOpen(false);
                        }}
                      >
                        {MenuIcon && <MenuIcon className="w-4 h-4" />}
                        <span>{menuItem.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        }

        if (item.disabled || !item.to) {
          return (
            <button
              key={item.id}
              type="button"
              disabled
              aria-label={expanded ? undefined : item.label}
              title={item.label}
              className="app-nav-item flex items-center gap-3 px-2.5 py-1.5 rounded-xl text-sm font-medium opacity-40 cursor-not-allowed"
            >
              {content}
            </button>
          );
        }

        return (
          <NavLink
            key={item.id}
            to={item.to}
            end={item.exact}
            aria-label={expanded ? undefined : item.label}
            title={expanded ? undefined : item.label}
            className={({ isActive }) =>
              cn(
                'app-nav-item flex items-center gap-3 px-2.5 py-1.5 rounded-xl text-sm font-medium transition-colors',
                isActive ? 'app-nav-item--active' : 'app-nav-item--idle'
              )
            }
            onClick={() => setMobileNavOpen(false)}
          >
            {content}
          </NavLink>
        );
      })}
    </nav>
  );

  const SidebarContent = (expanded) => {
    const extras = typeof sidebarExtras === 'function' ? sidebarExtras(expanded) : sidebarExtras;
    return (
    <div
      className={cn(
        'flex flex-col h-full px-3 py-4',
        expanded ? 'app-sidebar-expanded' : 'app-sidebar-collapsed'
      )}
    >
      <div className={cn('flex items-center', expanded ? 'justify-between gap-3' : 'flex-col gap-2')}>
        <div className={cn('flex items-center gap-3', !expanded && 'justify-center')}>
          <img
            src={`${process.env.PUBLIC_URL}/favicon.png`}
            alt="TickleGram"
            className="w-10 h-10 rounded-xl"
          />
          {expanded && (
            <div>
              <p className="text-base font-semibold leading-tight">TickleGram</p>
              <p className="text-xs text-[var(--tg-text-muted)]">Inbox Suite</p>
            </div>
          )}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={toggleSidebar}
          className="app-sidebar-expand-button shrink-0"
          aria-label={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
          title={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
        >
          {expanded ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </Button>
      </div>
      <div className="app-sidebar-scroll-region min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
        {renderNavList(expanded)}
        {extras && (
          <div className="mt-4">
            {extras}
          </div>
        )}
      </div>
      <div className="shrink-0 pt-3">
        {expanded ? (
          <div className="app-sidebar-account flex items-center gap-2 rounded-2xl bg-[var(--tg-surface-muted)] border border-[var(--tg-border-soft)] p-2">
            <button
              type="button"
              onClick={() => navigate('/profile')}
              className="app-sidebar-account__identity flex items-center gap-3 flex-1 min-w-0 text-left rounded-xl px-1 py-1"
              title="Open profile"
            >
              {renderAvatar('w-11 h-11')}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{user?.name}</p>
                <p className="text-xs text-[var(--tg-text-muted)] truncate">{roleLabel}</p>
              </div>
            </button>
            {renderLogoutButton({
              className: 'app-sidebar-logout text-[var(--tg-text-muted)] hover:text-rose-300',
              showLabel: true
            })}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => navigate('/profile')}
            className="flex items-center justify-center rounded-2xl bg-[var(--tg-surface-muted)] border border-[var(--tg-border-soft)] p-3 hover:border-[var(--tg-accent-soft)] transition"
          >
            {renderAvatar('w-12 h-12')}
          </button>
        )}
      </div>
    </div>
  );
  };

  return (
    <div className="app-shell flex min-h-screen w-full min-w-0 bg-[var(--tg-app-bg)] text-[var(--tg-text-primary)]">
      <aside
        className={cn(
          'app-sidebar hidden lg:flex',
          isSidebarExpanded ? 'app-sidebar--expanded' : 'app-sidebar--collapsed'
        )}
      >
        {SidebarContent(isSidebarExpanded)}
      </aside>

      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent side="left" className="p-6 w-[280px] bg-[var(--tg-sidebar-bg)] border-r border-[var(--tg-border-soft)]">
          {SidebarContent(true)}
        </SheetContent>
      </Sheet>

      <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col">
        <div className="app-shell__topbar sticky top-0 z-40 flex items-center justify-between border-b border-[var(--tg-border-soft)] bg-[var(--tg-surface)] px-3 py-2.5 lg:hidden">
          <div className="flex min-w-0 items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileNavOpen(true)}
              className="text-[var(--tg-text-primary)]"
            >
              <Menu className="w-5 h-5" />
            </Button>
            <span className="truncate font-semibold">TickleGram Inbox</span>
          </div>
        </div>
        <main className="app-shell__content flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          {children}
        </main>
      </div>
      {showInstagramManager && (
        <InstagramAccountManager onClose={() => setShowInstagramManager(false)} />
      )}
      {showFacebookManager && (
        <FacebookPageManager onClose={() => setShowFacebookManager(false)} />
      )}
      {showWhatsAppManager && (
        <WhatsAppConnectionManager onClose={() => setShowWhatsAppManager(false)} />
      )}
    </div>
  );
};

export default AppShell;
